import { useCallback, useEffect, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

// PR #67 — Brand ad-spend mappings. Each vendor account seen in this tenant's
// ad_spend_fact shows the canonical bridge (admin's decision) alongside the
// brand's own override (brand.ad_spend_match_rule), if any. The brand can
// override the canonical mapping or reset back to it. Overrides are sticky and
// surface in the public API immediately (brand_resolutions overlay).

interface MappingRow {
  vendor: string;
  vendor_account_id: string;
  fact_rows: number;
  first_period: string | null;
  last_period: string | null;
  total_spend_usd: string | number | null;
  vendor_display_name: string | null;
  vendor_state: string | null;
  canonical_license_number: string | null;
  canonical_license_dba: string | null;
  canonical_chain_id: string | null;
  canonical_chain_name: string | null;
  brand_license_number: string | null;
  brand_license_dba: string | null;
  brand_chain_id: string | null;
  brand_chain_id_kind: "canonical" | "brand_only" | null;
  brand_chain_name: string | null;
  rule_notes: string | null;
  rule_updated_at: string | null;
}

type VendorFilter = "all" | "dutchie" | "iheartjane" | "surfside";
const VENDOR_FILTERS: VendorFilter[] = ["all", "dutchie", "iheartjane", "surfside"];

type RowState = "override" | "inherited" | "unresolved";

function rowState(r: MappingRow): RowState {
  if (r.brand_license_number || r.brand_chain_id) return "override";
  if (r.canonical_license_number || r.canonical_chain_id) return "inherited";
  return "unresolved";
}

function money(v: string | number | null): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : parseFloat(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function rowKey(r: { vendor: string; vendor_account_id: string }): string {
  return `${r.vendor}::${r.vendor_account_id}`;
}

export default function BrandMappingsAdSpend({ slug }: { slug: string }) {
  const toast = useToast();

  const [vendor, setVendor] = useState<VendorFilter>("all");
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Rule editor dialog state.
  const [editTarget, setEditTarget] = useState<MappingRow | null>(null);
  const [editLicense, setEditLicense] = useState("");
  const [editChainId, setEditChainId] = useState("");
  const [editChainKind, setEditChainKind] = useState<"canonical" | "brand_only">("canonical");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = vendor === "all" ? "" : `?vendor=${vendor}`;
      const r = await brandApi.get(`/api/brand/${encodeURIComponent(slug)}/ad-spend/mappings/list${qs}`);
      setRows(r.rows || []);
    } catch (err) {
      toast.push(`Failed to load mappings: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [slug, vendor, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (r: MappingRow) => {
    setEditTarget(r);
    // Seed from the brand override if present, else the canonical suggestion.
    setEditLicense(r.brand_license_number ?? r.canonical_license_number ?? "");
    setEditChainId(r.brand_chain_id ?? r.canonical_chain_id ?? "");
    setEditChainKind(r.brand_chain_id_kind ?? "canonical");
    setEditNotes(r.rule_notes ?? "");
  };

  const submitRule = async () => {
    if (!editTarget) return;
    const license = editLicense.trim();
    const chainId = editChainId.trim();
    if (!license && !chainId) {
      toast.push("Supply a license number and/or a chain id.", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await brandApi.post(`/api/brand/${encodeURIComponent(slug)}/ad-spend/mappings/rule`, {
        vendor: editTarget.vendor,
        vendor_account_id: editTarget.vendor_account_id,
        license_number: license || undefined,
        chain_id: chainId || undefined,
        chain_id_kind: chainId ? editChainKind : undefined,
        notes: editNotes.trim() || undefined,
      });
      toast.push(`Override saved — ${r.fact_rows_stamped} fact row(s) updated`, "success");
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.push(`Save failed: ${(err as Error).message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const resetToCanonical = async (r: MappingRow) => {
    if (!window.confirm(`Reset ${r.vendor} / ${r.vendor_account_id} to the canonical mapping? Your override is removed.`))
      return;
    setBusyKey(rowKey(r));
    try {
      const res = await brandApi.del(
        `/api/brand/${encodeURIComponent(slug)}/ad-spend/mappings/rule/${encodeURIComponent(r.vendor)}/${encodeURIComponent(r.vendor_account_id)}`
      );
      toast.push(`Reset to canonical — ${res.fact_rows_cleared} fact row(s) cleared`, "success");
      await load();
    } catch (err) {
      toast.push(`Reset failed: ${(err as Error).message}`, "error");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Ad-spend mappings</h2>
        <p className="text-sm text-gray-600 mt-1">
          Each vendor account in your ad-spend uploads resolves to a retail license or chain. We inherit the canonical
          mapping by default; override it here when your view differs. Overrides are sticky and show up in your API feed
          right away.
        </p>
      </div>

      <div className="flex items-center gap-2">
        {VENDOR_FILTERS.map((v) => (
          <button
            key={v}
            onClick={() => setVendor(v)}
            className={`px-3 py-1 rounded-full text-xs font-medium border capitalize transition-colors ${
              vendor === v
                ? "bg-accent-500 text-white border-accent-500"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {v}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-600">
            No ad-spend yet. Upload vendor reports to populate your mappings.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Vendor account</th>
                <th className="text-left px-4 py-2 font-medium">Effective mapping</th>
                <th className="text-left px-4 py-2 font-medium">State</th>
                <th className="text-right px-4 py-2 font-medium">Spend</th>
                <th className="text-left px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const k = rowKey(r);
                const busy = busyKey === k;
                const state = rowState(r);
                const effLicense = r.brand_license_number ?? r.canonical_license_number;
                const effLicenseDba = r.brand_license_number ? r.brand_license_dba : r.canonical_license_dba;
                const effChainName =
                  r.brand_chain_id ? r.brand_chain_name : r.canonical_chain_id ? r.canonical_chain_name : null;
                const effChainId = r.brand_chain_id ?? r.canonical_chain_id;
                return (
                  <tr key={k} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{r.vendor_display_name || r.vendor_account_id}</div>
                      <div className="text-xs text-gray-500">
                        <span className="capitalize">{r.vendor}</span> ·{" "}
                        <span className="font-mono">{r.vendor_account_id}</span>
                        {r.vendor_state ? ` · ${r.vendor_state}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {effLicense ? (
                        <div className="text-gray-700">
                          <span className="font-mono">{effLicense}</span>
                          {effLicenseDba ? ` · ${effLicenseDba}` : ""}
                        </div>
                      ) : null}
                      {effChainId ? (
                        <div className="text-gray-700">chain: {effChainName || effChainId}</div>
                      ) : null}
                      {!effLicense && !effChainId && <span className="text-xs text-gray-400">unresolved</span>}
                    </td>
                    <td className="px-4 py-2">
                      {state === "override" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-violet-100 text-violet-800 border-violet-200">
                          Your override
                        </span>
                      ) : state === "inherited" ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-700 border-gray-200">
                          Inherited
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-100 text-amber-800 border-amber-200">
                          Unresolved
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700">{money(r.total_spend_usd)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Button variant="primary" size="sm" disabled={busy} onClick={() => openEditor(r)}>
                          {state === "override" ? "Edit" : state === "inherited" ? "Override" : "Add mapping"}
                        </Button>
                        {state === "override" && (
                          <Button variant="secondary" size="sm" disabled={busy} onClick={() => resetToCanonical(r)}>
                            {busy ? "…" : "Reset to canonical"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !saving && setEditTarget(null)}
        >
          <div className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">
              {rowState(editTarget) === "override" ? "Edit override" : "Override mapping"}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              <span className="capitalize">{editTarget.vendor}</span> account{" "}
              <span className="font-mono">{editTarget.vendor_account_id}</span>
              {editTarget.vendor_display_name ? ` — ${editTarget.vendor_display_name}` : ""}. Supply a license number
              and/or a chain id. Leave both blank and use “Reset to canonical” instead to inherit.
            </p>
            {(editTarget.canonical_license_number || editTarget.canonical_chain_id) && (
              <p className="mt-2 text-xs text-gray-500">
                Canonical:{" "}
                {editTarget.canonical_license_number ? (
                  <span className="font-mono">{editTarget.canonical_license_number}</span>
                ) : (
                  <span>chain {editTarget.canonical_chain_name || editTarget.canonical_chain_id}</span>
                )}
              </p>
            )}
            <label className="mt-4 block text-xs text-gray-500">
              License number
              <input
                type="text"
                value={editLicense}
                onChange={(e) => setEditLicense(e.target.value)}
                placeholder="C10-0000000-LIC"
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1 font-mono"
              />
            </label>
            <label className="mt-3 block text-xs text-gray-500">
              Chain id (optional)
              <input
                type="text"
                value={editChainId}
                onChange={(e) => setEditChainId(e.target.value)}
                placeholder="uuid"
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1 font-mono"
              />
            </label>
            {editChainId.trim() && (
              <label className="mt-3 block text-xs text-gray-500">
                Chain kind
                <select
                  value={editChainKind}
                  onChange={(e) => setEditChainKind(e.target.value as "canonical" | "brand_only")}
                  className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
                >
                  <option value="canonical">canonical (core.retail_chain)</option>
                  <option value="brand_only">brand_only (your private chain)</option>
                </select>
              </label>
            )}
            <label className="mt-3 block text-xs text-gray-500">
              Notes (optional)
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={2}
                className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditTarget(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitRule}
                disabled={saving || (!editLicense.trim() && !editChainId.trim())}
              >
                {saving ? "Saving…" : "Save override"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
